import { store } from './store.js';
/**
 * printProblem.js - Generador de PDFs para problemas matemáticos
 * 
 * Genera un PDF imprimible basado en la plantilla "plantilla problema.pdf"
 * con el enunciado del problema, barras de escala y datos dinámicos.
 * 
 * Requiere: jsPDF library (https://github.com/parallax/jsPDF)
 */

// Imagen de prueba (Base64) - Placeholder temporal
const TEST_IMAGE_URL = 'https://raw.githubusercontent.com/placeholder/test-image.jpg';

/**
 * Genera y descarga un PDF del problema actual
 * @param {Object} problem - El problema actual con statement, data, etc.
 * @param {number} problemNumber - Número del problema (1-5)
 * @param {boolean} preview - Si es true, abre el PDF en una nueva pestaña para previsualizar
 */
async function printProblemToPDF(problem, problemNumber, preview = false) {
    // Verificar que jsPDF está disponible
    if (typeof window.jspdf === 'undefined' && typeof jsPDF === 'undefined') {
        try {
            const jspdfModule = await import('jspdf');
            window.jspdf = { jsPDF: jspdfModule.jsPDF };
        } catch (err) {
            console.error('jsPDF no se pudo cargar:', err);
            alert('Error: La librería de PDF no está disponible.');
            return;
        }
    }

    const { jsPDF } = window.jspdf || { jsPDF: window.jsPDF };

    // Crear documento A4
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // Obtener datos del problema con fallback seguro de idioma
    const lang = (typeof window !== 'undefined' && window.currentLanguage) || (typeof currentLanguage !== 'undefined' ? currentLanguage : 'es-ES');
    const statement = problem?.statement?.[lang] || problem?.statement?.['es-ES'];
    const usefulData = problem?.data?.useful || [0, 0];
    const elementName = getObjectName(problem);
    const maxValue = Math.max(...usefulData);

    // =====================================
    // HEADER: Título con contexto y nivel
    // =====================================
    // Obtener el nombre del contexto desde CONTEXTS para tener el título correcto con caracteres especiales
    const contexts = (typeof window !== 'undefined' && window.CONTEXTS) ? window.CONTEXTS : (typeof CONTEXTS !== 'undefined' ? CONTEXTS : []);
    const contextData = Array.isArray(contexts) ? contexts.find(c => c.id === problem?.contextId) : null;
    const contextName = contextData
        ? (contextData?.title?.[lang] || contextData?.title?.['es-ES'])
        : (problem?.contextId || 'General');
    // Mapeo de niveles para mostrar nombres correctos en español
    const LEVEL_NAMES_PDF = {
        'beginner': 'APRENDIZ',
        'advanced': 'AVANZADO',
        'expert': 'EXPERTO'
    };
    const levelName = LEVEL_NAMES_PDF[problem.level] || problem.level || 'APRENDIZ';

    // Header with two font sizes
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const mainTitle = 'DETECTIVES MATEMÁTICOS 1º';
    doc.text(mainTitle, margin, 20);

    // Get width of main title to position the second part
    const mainTitleWidth = doc.getTextWidth(mainTitle);

    // Second part with smaller font
    doc.setFontSize(12);
    const contextUppercase = contextName.toUpperCase();
    const subtitle = ` - ${contextUppercase} - Nivel ${levelName}`;
    doc.text(subtitle, margin + mainTitleWidth + 2, 20, { maxWidth: contentWidth - mainTitleWidth - 2 });

    // =====================================
    // SECCIÓN: ENUNCIADO + IMAGEN (ampliada)
    // =====================================
    const enunciadoY = 28;
    const enunciadoHeight = 65; // Aumentado de 55 a 65mm
    const tableWidth = contentWidth; // Usar todo el ancho disponible

    // Marco exterior
    doc.setLineWidth(0.3);
    doc.rect(margin, enunciadoY, tableWidth, enunciadoHeight);

    // División interna: ENUNCIADO (izquierda) | IMAGEN (derecha) - 50/50
    const enunciadoWidth = tableWidth * 0.5;
    const imagenWidth = tableWidth * 0.5;

    // Línea divisoria vertical
    doc.line(margin + enunciadoWidth, enunciadoY, margin + enunciadoWidth, enunciadoY + enunciadoHeight);

    // Texto del enunciado (con word wrap) - SIEMPRE TAMAÑO 16
    doc.setFont('times', 'italic');
    doc.setFontSize(16);
    doc.text(statement, margin + 4, enunciadoY + 8, { maxWidth: enunciadoWidth - 8 });

    // Insertar imágenes en el área derecha
    const imgX = margin + enunciadoWidth + 3;
    const imgY = enunciadoY + 3;
    const imgW = imagenWidth - 6;
    const imgH = enunciadoHeight - 6;

    // Lógica para mostrar 1 o 2 imágenes
    if (problem.images && problem.images.length >= 2) {
        // --- MODO 2 IMÁGENES (Side-by-Side) ---
        const gap = 2;
        const singleImgW = (imgW - gap) / 2;

        // Imagen 1 (Izquierda)
        try {
            const img1 = new Image();
            img1.src = problem.images[0];

            const maxW = singleImgW;
            const maxH = imgH;
            const imgW1 = img1.naturalWidth || img1.width || 800;
            const imgH1 = img1.naturalHeight || img1.height || 600;
            const ratio1 = imgW1 / imgH1;

            let finalW1 = maxW;
            let finalH1 = maxW / ratio1;
            if (finalH1 > maxH) {
                finalH1 = maxH;
                finalW1 = maxH * ratio1;
            }

            const offsetX1 = (maxW - finalW1) / 2;
            const offsetY1 = (maxH - finalH1) / 2;

            doc.addImage(problem.images[0], 'JPEG', imgX + offsetX1, imgY + offsetY1, finalW1, finalH1);
        } catch (e) {
            console.warn('Error cargando img 1', e);
            doc.setFillColor(240, 240, 240);
            doc.rect(imgX, imgY, singleImgW, imgH, 'F');
        }

        // Imagen 2 (Derecha)
        try {
            const img2 = new Image();
            img2.src = problem.images[1];

            const maxW = singleImgW;
            const maxH = imgH;
            const imgW2 = img2.naturalWidth || img2.width || 800;
            const imgH2 = img2.naturalHeight || img2.height || 600;
            const ratio2 = imgW2 / imgH2;

            let finalW2 = maxW;
            let finalH2 = maxW / ratio2;
            if (finalH2 > maxH) {
                finalH2 = maxH;
                finalW2 = maxH * ratio2;
            }

            const offsetX2 = (maxW - finalW2) / 2;
            const offsetY2 = (maxH - finalH2) / 2;

            doc.addImage(problem.images[1], 'JPEG', imgX + singleImgW + gap + offsetX2, imgY + offsetY2, finalW2, finalH2);
        } catch (e) {
            console.warn('Error cargando img 2', e);
            doc.setFillColor(240, 240, 240);
            doc.rect(imgX + singleImgW + gap, imgY, singleImgW, imgH, 'F');
        }

    } else if (problem.images && problem.images.length >= 1) {
        // --- MODO 1 IMAGEN (Desde problem.images[0]) ---
        try {
            // Crear imagen temporal para obtener dimensiones reales
            const img = new Image();
            img.src = problem.images[0];

            // Calcular dimensiones manteniendo aspect ratio
            const maxWidth = imgW;
            const maxHeight = imgH;

            // Obtener dimensiones de la imagen (usar naturalWidth/Height si está disponible)
            const imgWidth = img.naturalWidth || img.width || 800;
            const imgHeight = img.naturalHeight || img.height || 600;

            // Calcular aspect ratio
            const aspectRatio = imgWidth / imgHeight;

            // Calcular dimensiones finales
            let finalWidth = maxWidth;
            let finalHeight = maxWidth / aspectRatio;

            // Si la altura excede el máximo, ajustar por altura
            if (finalHeight > maxHeight) {
                finalHeight = maxHeight;
                finalWidth = maxHeight * aspectRatio;
            }

            // Centrar la imagen en el espacio disponible
            const offsetX = (maxWidth - finalWidth) / 2;
            const offsetY = (maxHeight - finalHeight) / 2;

            doc.addImage(problem.images[0], 'JPEG', imgX + offsetX, imgY + offsetY, finalWidth, finalHeight);
        } catch (e) {
            // Si falla, mostrar placeholder
            doc.setFillColor(245, 245, 245);
            doc.rect(imgX, imgY, imgW, imgH, 'F');
            doc.setFontSize(10);
            doc.setTextColor(150, 150, 150);
            doc.text('IMAGEN', imgX + (imgW / 2) - 8, imgY + (imgH / 2));
            doc.setTextColor(0, 0, 0);
        }
    } else {
        // Placeholder visual
        doc.setFillColor(245, 245, 245);
        doc.rect(imgX, imgY, imgW, imgH, 'F');
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text('SIN IMAGEN', imgX + (imgW / 2) - 10, imgY + (imgH / 2));
        doc.setTextColor(0, 0, 0);
    }

    // =====================================
    // SECCIÓN: Instrucción de subrayado (modificado)
    // =====================================
    const preguntaY = enunciadoY + enunciadoHeight + 9;
    doc.setFontSize(14);
    doc.setFont('times', 'italic');
    const instruccionSubrayado = 'Vuelve a leer el texto y con un color, subraya la pregunta. Después, con un color diferente, subraya los datos relacionados con la pregunta.';
    doc.text(instruccionSubrayado, margin, preguntaY, { maxWidth: contentWidth });

    // =====================================
    // SECCIÓN: Datos y barras
    // =====================================
    const barrasY = preguntaY + 16;
    const barrasSectionHeight = 55;

    // Marco de la sección
    doc.rect(margin, barrasY, contentWidth, barrasSectionHeight);

    // Título centrado
    doc.setFontSize(14);
    doc.setFont('times', 'italic');
    const barrasTitle = 'Escribe los datos y colorea cada barra hasta llegar a su valor numérico';
    const titleWidth = doc.getTextWidth(barrasTitle);
    doc.text(barrasTitle, margin + (contentWidth - titleWidth) / 2, barrasY + 6);

    // Barra A (alineada con línea inferior de la barra de escala)
    const barraAY = barrasY + 14;
    const scaleBarHeight = 8;
    doc.setFont('times', 'italic');
    doc.setFontSize(16);
    // A. y línea alineados con la parte inferior de la barra (Movido 10pt a la izquierda)
    doc.text('A.', margin + 5, barraAY + scaleBarHeight - 1);
    // Línea para escribir el dato (Comienza 10pt antes, termina igual -> más larga)
    doc.setLineWidth(0.3);
    doc.line(margin + 12, barraAY + scaleBarHeight - 1, margin + 45, barraAY + scaleBarHeight - 1);
    drawScaleBar(doc, margin + 55, barraAY, 95, scaleBarHeight, maxValue);

    // Barra B (alineada con línea inferior de la barra de escala)
    const barraBY = barrasY + 30;
    doc.setFontSize(16);
    doc.setFont('times', 'italic');
    // B. y línea alineados con la parte inferior de la barra (Movido 10pt a la izquierda)
    doc.text('B.', margin + 5, barraBY + scaleBarHeight - 1);
    // Línea para escribir el dato
    doc.line(margin + 12, barraBY + scaleBarHeight - 1, margin + 45, barraBY + scaleBarHeight - 1);
    drawScaleBar(doc, margin + 55, barraBY, 95, scaleBarHeight, maxValue);

    // "El DATO / LA CANTIDAD más grande es" con recuadro (separado 4 puntos de la escala)
    const cantidadY = barrasY + 50;
    doc.setFontSize(14);
    doc.setFont('times', 'italic');
    const textoCantidad = 'EL DATO/LA CANTIDAD más grande es';
    doc.text(textoCantidad, margin + 15, cantidadY);
    // Recuadro a 10 puntos del texto (Ancho aumentado en 25pt: 20 -> 45)
    const textoAncho = doc.getTextWidth(textoCantidad);
    doc.rect(margin + 15 + textoAncho + 5, cantidadY - 5, 45, 8);

    // =====================================
    // SECCIÓN: Razonamiento (texto modificado)
    // =====================================
    const razonamientoY = barrasY + barrasSectionHeight + 6;
    doc.setFontSize(14);
    doc.setFont('times', 'italic');

    // Línea 1 con el nombre del elemento - TEXTO MODIFICADO
    // Extraer el elemento de la pregunta (ej: "frutas" en "¿Cuántas frutas hay en total?")
    const questionElement = extractElementFromQuestion(problem) || elementName;
    const razonamientoText1 = `Ahora piensa. Cuando acabes de investigar este problema, ¿el número de ${questionElement} será MAYOR o MENOR que el DATO más grande?`;
    
    // Splittext is needed here to calculate height for the next box
    const razonamientoLines1 = doc.splitTextToSize(razonamientoText1, contentWidth);
    doc.text(razonamientoLines1, margin, razonamientoY);

    // Calcular posición del recuadro del recordatorio (lado a lado con MAYOR/MENOR)
    const recordatorioBaseY = razonamientoY + (razonamientoLines1.length * 5) + 8;

    // Primero calcular el texto del recordatorio
    doc.setFontSize(13);
    const recordatorioText = 'Recuerda que, si el resultado que esperas es MAYOR, deberás realizar una SUMA. Y que, si el resultado que esperas es MENOR, deberás realizar una RESTA.';

    // Calcular ancho necesario para MAYOR/MENOR
    doc.setFontSize(16);
    const mayorWidth = doc.getTextWidth('MAYOR');
    const menorWidth = doc.getTextWidth('MENOR');
    const maxLabelWidth = Math.max(mayorWidth, menorWidth);

    // Posición X del recuadro: margin + 10 (texto) + maxLabelWidth + 6 (separación) + 6 (cuadrado) + 6 (separación)
    const recordatorioX = margin + 10 + maxLabelWidth + 6 + 6 + 6;

    // El recuadro se extiende hasta el margen derecho
    const recordatorioWidth = (margin + contentWidth) - recordatorioX;

    // Ajustar tamaño de fuente para el recordatorio
    doc.setFontSize(13);
    const recordatorioLines = doc.splitTextToSize(recordatorioText, recordatorioWidth - 8);

    // Calcular altura del recuadro según líneas
    const recordatorioBoxHeight = (recordatorioLines.length * 5) + 8;
    const recordatorioY = recordatorioBaseY - 6;

    // Dibujar recuadro del recordatorio
    doc.setLineWidth(0.3);
    doc.rect(recordatorioX, recordatorioY, recordatorioWidth, recordatorioBoxHeight);
    doc.text(recordatorioLines, recordatorioX + 4, recordatorioY + 6);

    // MAYOR / MENOR centrados verticalmente con el recuadro
    const opcionesCentroY = recordatorioY + (recordatorioBoxHeight / 2);
    doc.setFontSize(16);
    doc.text('MAYOR', margin + 10, opcionesCentroY - 4);
    // Cuadrado después del texto con 6 puntos de separación
    doc.rect(margin + 10 + mayorWidth + 6, opcionesCentroY - 8, 6, 6);

    doc.text('MENOR', margin + 10, opcionesCentroY + 6);
    // Cuadrado después del texto con 6 puntos de separación
    doc.rect(margin + 10 + menorWidth + 6, opcionesCentroY + 2, 6, 6);

    // =====================================
    // SECCIÓN: Cálculos (sin título)
    // =====================================
    const calculosY = Math.max(opcionesCentroY + 20, recordatorioY + recordatorioBoxHeight + 8);

    // Nueva tabla de cálculos con formato D/U
    const gridX = margin + 30;
    const gridY = calculosY + 8;
    const cellSize = 12;
    const gridCols = 3; // Columna operador + D + U
    const numDataRows = usefulData.length + 1; // Datos + fila para +1 (acarreo)
    const totalRows = numDataRows + 1; // + fila resultado

    // Dibujar las letras D y U encima de las columnas (EN NEGRITA)
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');

    // D en rojo encima de la penúltima columna
    doc.setTextColor(198, 40, 40); // #c62828
    doc.text('D', gridX + cellSize + (cellSize / 2) - 3, gridY - 3);

    // U en azul encima de la última columna
    doc.setTextColor(21, 101, 192); // #1565c0
    doc.text('U', gridX + (2 * cellSize) + (cellSize / 2) - 3, gridY - 3);

    doc.setTextColor(0, 0, 0); // Restaurar color negro

    // Dibujar filas de datos (líneas normales)
    doc.setLineWidth(0.3);
    for (let row = 0; row <= numDataRows; row++) {
        doc.line(gridX, gridY + (row * cellSize), gridX + (gridCols * cellSize), gridY + (row * cellSize));
    }

    // Línea gruesa (3x) separando datos del resultado
    doc.setLineWidth(1.2);
    doc.line(gridX, gridY + (numDataRows * cellSize), gridX + (gridCols * cellSize), gridY + (numDataRows * cellSize));

    // Fila de resultado (línea normal debajo)
    doc.setLineWidth(0.3);
    doc.line(gridX, gridY + (totalRows * cellSize), gridX + (gridCols * cellSize), gridY + (totalRows * cellSize));

    // Líneas verticales
    for (let col = 0; col <= gridCols; col++) {
        doc.line(gridX + (col * cellSize), gridY, gridX + (col * cellSize), gridY + (totalRows * cellSize));
    }

    // =====================================
    // Solución (alineado con última fila de la tabla)
    // =====================================
    const solucionY = gridY + (totalRows * cellSize) - (cellSize / 2) + 4;
    const solucionX = gridX + (gridCols * cellSize) + 10;
    doc.setFontSize(16);
    doc.setFont('times', 'italic');
    doc.text('Solución =', solucionX, solucionY);
    doc.line(solucionX + 25, solucionY, solucionX + 80, solucionY);

    // =====================================
    // FOOTER (solo SarabiaWork)
    // =====================================
    doc.setFontSize(7);
    doc.setFont('times', 'italic');
    doc.setTextColor(150, 150, 150);
    // Use window.APP_BRANDING or a fallback if not defined
    const footerText = (typeof APP_BRANDING !== 'undefined' && APP_BRANDING.FOOTER_TEXT) ? APP_BRANDING.FOOTER_TEXT : 'Detectimáticas de 1º - SarabiaWork';
    doc.text(footerText, pageWidth / 2, pageHeight - 7, { align: 'center' });

    // Generar PDF
    const fileName = `Problema_${problemNumber}_${problem.contextId || 'problema'}.pdf`;

    if (preview) {
        // Previsualizar: abrir en nueva pestaña
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
        console.log(`PDF abierto para previsualización: ${fileName}`);
    } else {
        // Descargar directamente
        doc.save(fileName);
        console.log(`PDF descargado: ${fileName}`);
    }
}

/**
 * Dibuja una barra de escala horizontal
 * @param {jsPDF} doc - Documento jsPDF
 * @param {number} x - Posición X
 * @param {number} y - Posición Y
 * @param {number} width - Ancho de la barra
 * @param {number} height - Alto de la barra
 * @param {number} maxValue - Valor máximo de la escala
 */
function drawScaleBar(doc, x, y, width, height, maxValue) {
    // Fondo blanco de la barra
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(x, y, width, height, 'FD');

    // Calcular escala apropiada
    let scaleMax = 10;
    if (maxValue <= 10) {
        scaleMax = 10;
    } else if (maxValue <= 20) {
        scaleMax = 20;
    } else if (maxValue <= 50) {
        scaleMax = 50;
    } else {
        scaleMax = 100;
    }

    // Dibujar unidades (ticks pequeños)
    doc.setLineWidth(0.1);
    const unitWidth = width / scaleMax;
    for (let j = 1; j < scaleMax; j++) {
        // No dibujar sobre las marcas de 5 o 10 que serán más gruesas
        if (j % 5 !== 0) {
            const posUnitX = x + (j * unitWidth);
            doc.line(posUnitX, y + height - 1.5, posUnitX, y + height);
        }
    }

    // Dibujar divisiones de 5 y 10 (ticks más grandes y etiquetas)
    doc.setLineWidth(0.3);
    doc.setFontSize(8);
    doc.setFont('times', 'italic');
    doc.setTextColor(0, 0, 0);

    for (let i = 0; i <= scaleMax; i += 5) {
        const posX = x + (i * unitWidth);
        const value = i;

        // Marca más larga para múltiplos de 5
        const tickHeight = (i % 10 === 0) ? 3 : 2.2;
        doc.line(posX, y + height - tickHeight, posX, y + height);

        // Número debajo
        const textWidth = doc.getTextWidth(String(value));
        doc.text(String(value), posX - (textWidth / 2), y + height + 4.5);
    }
}

/**
 * Función para abrir el diálogo de impresión del problema actual
 */
function printCurrentProblem() {
    if (!store.state || !store.state.problems || store.state.problems.length === 0) {
        alert('No hay ningún problema cargado para imprimir.');
        return;
    }

    const currentProblem = store.state.problems[store.state.currentProblem];
    const problemNumber = store.state.currentProblem + 1;

    printProblemToPDF(currentProblem, problemNumber, false); // false = descargar
}

/**
 * Función para previsualizar el PDF del problema actual en una nueva pestaña
 */
function previewProblemPDF() {
    if (!store.state || !store.state.problems || store.state.problems.length === 0) {
        alert('No hay ningún problema cargado para imprimir.');
        return;
    }

    const currentProblem = store.state.problems[store.state.currentProblem];
    const problemNumber = store.state.currentProblem + 1;

    printProblemToPDF(currentProblem, problemNumber, true); // true = previsualizar
}

/**
 * Extrae el elemento sobre el que se pregunta (sustantivo después de cuántos/cuántas)
 * @param {Object} problem - El problema con la pregunta
 * @returns {string} - El elemento de la pregunta (ej: "frutas" en "¿Cuántas frutas hay en total?")
 */
function extractElementFromQuestion(problem) {
    const lang = (typeof window !== 'undefined' && window.currentLanguage) || (typeof currentLanguage !== 'undefined' ? currentLanguage : 'es-ES');
    const question = problem.question || problem.statement?.[lang] || problem.statement?.['es-ES'] || '';

    // Extraer el sustantivo que sigue a "cuántos", "cuántas", "cuánto", "cuánta"
    // Patrón: ¿Cuántos/as + [sustantivo] + ...?
    const pattern = /¿\s*cuánt[oa]s?\s+([a-zñáéíóúü]+)/i;
    const match = question.match(pattern);

    if (match && match[1]) {
        return match[1];
    }

    // Fallback: intentar extraer cualquier sustantivo después de un número en la pregunta
    const fallbackPattern = /\d+\s+([a-zñáéíóúü]+)/i;
    const fallbackMatch = question.match(fallbackPattern);

    if (fallbackMatch && fallbackMatch[1]) {
        return fallbackMatch[1];
    }

    return null; // Si no se encuentra, usar el fallback del código principal
}

// Exportar funciones globalmente
window.printProblemToPDF = printProblemToPDF;
window.printCurrentProblem = printCurrentProblem;
window.previewProblemPDF = previewProblemPDF;

console.log('printProblem.js cargado - Generador de PDFs disponible');